<template>
    <div class="media-body">
        <a class="name h6 mb-0 text-sm" v-if="showName">
            {{ block.name.toUpperCase() }}
            <small v-if="blockFinished"> {{ $t('blocks.block_finished') }}</small>
        </a>

        <div>
            <small class="font-weight-bold" v-html="infoItems.join(' | ')"></small>
        </div>

        <div v-if="hasToShortTimePerPhoto" class="short-duration-per-photo">
            <icon class="block-error" name="exclamation-circle"></icon>
            <strong>{{ $t('blocks.short_duration_per_photo_warning') }}</strong>
        </div>
    </div>
</template>

<script>

    import {getDurationPerImage} from "../utils/duration";
    import FileMixin from "../mixins/FileMixin";
    import UtilMixin from "../designer/mixins/UtilMixin";
    import {isInfiniteBlock} from "../utils/getMaxDurationForBlock";

    export default {

        props: {
            showName:{
                type: Boolean,
                default: true
            },
            block: {
                required: true,
                type: Object
            },
            blockFinished: {
                required: false,
                type: Boolean,
                default: false
            },
            ceremony: {
                required: true,
                type: Object
            }
        },

        mixins: [
            FileMixin,
            UtilMixin,
        ],

        computed: {
            infoItems() {
                let items = [];
                const parent = this.isParentBlock() ? this.block : this.block.parent_block;
                const repeatAudioSetting = parent ? this.getSettingByName('repeat_audio', parent.settings) : null;

                if (!(
                    repeatAudioSetting && repeatAudioSetting.value === '1') &&
                    (
                        (this.block.parent_block && this.block.parent_block.type !== 6) ||
                        !this.block.parent_block
                    )) {
                    items.push((isInfiniteBlock(this.block) ? '~ ' : '') + this.$root.minutesToTimeInput(this.block.total_duration) + ' MIN')
                }

                // Check if the block or the first block does not use files from another block
                if (parent && parent.subblocks.length && !parent.subblocks[0].use_files_from_block_id) {
                    if (this.isParentBlock()) {
                        if (this.block.files.filter(file => this.isAudio(file)).length) {
                            items.push(`<svg aria-hidden="true" width="12" height="12" viewBox="0 0 512 512" focusable="false" class="fa-icon"><g><path d="M470.4 1.5c3-1 6.3-1.5 9.6-1.5 17.7 0 32 14.3 32 32v352c0 35.3-43 64-96 64s-96-28.7-96-64 43-64 96-64c11 0.1 21.7 1.4 32 3.9v-184.6l-256 75v233.7c0 35.3-43 64-96 64s-96-28.7-96-64 43-64 96-64c11 0.1 21.7 1.4 32 3.9v-261.4c0-14.3 9.4-26.4 22.4-30.5z"></path></g></svg>`)
                        }

                        if (this.subBlocks.length === 1) {
                            // media blok should only have one subblock.
                            const subBlock = this.subBlocks[0];
                            let subBlockInfo = [];

                            switch (subBlock.type) {
                                case 1:
                                    subBlockInfo = this.getImageInfo(subBlock);
                                    break;
                                case 2:
                                    subBlockInfo = this.getVideoInfo(subBlock);
                                    break;
                            }

                            items = [
                                ...items,
                                ...subBlockInfo
                            ];
                        }
                    } else {
                        if (this.block.parent_block && this.block.parent_block.files.filter(file => this.isAudio(file)).length) {
                            items.push(`<svg aria-hidden="true" width="12" height="12" viewBox="0 0 512 512" focusable="false" class="fa-icon"><g><path d="M470.4 1.5c3-1 6.3-1.5 9.6-1.5 17.7 0 32 14.3 32 32v352c0 35.3-43 64-96 64s-96-28.7-96-64 43-64 96-64c11 0.1 21.7 1.4 32 3.9v-184.6l-256 75v233.7c0 35.3-43 64-96 64s-96-28.7-96-64 43-64 96-64c11 0.1 21.7 1.4 32 3.9v-261.4c0-14.3 9.4-26.4 22.4-30.5z"></path></g></svg>`)
                        }

                        let blockInfo = [];

                        switch (this.block.type) {
                            case 1:
                                blockInfo = this.getImageInfo(this.block);
                                break;
                            case 2:
                                blockInfo = this.getVideoInfo(this.block);
                                break;
                        }

                        items = [
                            ...items,
                            ...blockInfo
                        ];

                        if(this.block.has_manual_action){
                            items.push(this.$t('blocks.manual'));
                        }
                    }
                }

                return items
            },

            subBlocks() {
                if (!this.block || !this.block.subblocks) {
                    return [];
                }

                return this.block.subblocks.filter(subblock => {
                    return subblock.files.length;
                });
            },

            hasToShortTimePerPhoto() {
                if (this.isParentBlock()) {
                    if (this.subBlocks.length && this.subBlocks[0].type === 1/*1 = Image*/) {
                        return !this.subBlocks[0].has_manual_action && this.subBlocks[0].files.length && Number(this.getDurationPerImageInBlock(this.subBlocks[0])) <= 3;
                    }

                    return false;
                }

                if (this.block.type !== 1/*1 = Image*/) {
                    return false;
                }

                return !this.block.has_manual_action && this.block.files.length && Number(this.getDurationPerImageInBlock(this.block)) <= 3;
            }
        },

        methods: {
            getImageInfo(block) {
                if (block.files.length <= 0 || block.has_manual_action) {
                    return [];
                }

                const shouldRepeat = this.getSettingByName('repeat_photos', block.settings);

                return [
                    `${block.files.length} ${this.$t('blocks.photo')}${block.files.length > 1 ? this.$t('blocks.plural_suffix') : ''}`,
                    `${this.$root.formatNumber(this.getDurationPerImageInBlock(block), 1)} ${this.$t('blocks.seconds_per_photo')}`,
                    shouldRepeat && shouldRepeat.value === '1' ? this.$t('blocks.repeat_photos') : undefined
                ].filter(Boolean);
            },

            getVideoInfo(block) {
                if (block.files.length <= 0) {
                    return [];
                }

                const shouldRepeat = this.getSettingByName('repeat_videos', block.settings);

                return [
                    `${block.files.length} ${this.$t('blocks.video')}${block.files.length > 1 ? this.$t('blocks.plural_suffix') : ''}`,
                    shouldRepeat && shouldRepeat.value === '1' ? this.$t('blocks.repeat_videos') : undefined
                ].filter(Boolean);
            },

            isParentBlock() {
                return this.block.parent_id === null;
            },

            getDurationPerImageInBlock(block) {
                const parentBlock = this.ceremony.blocks.find(parentBlockToFind => parentBlockToFind.id === block.parent_id);

                return getDurationPerImage(block, parentBlock ? parentBlock.files : []);
            },
        }
    }
</script>

<style lang="scss" scoped>
    .short-duration-per-photo {
        margin-top: 10px;
        font-size: 13px;
        display: flex;
        align-items: center;

        svg{
            margin-right: 5px;
            color: #ffad0a;
        }
    }
</style>
